import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Check, CheckCircle2, Gauge, History, Info, Paperclip } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/ui/Loader";
import { formatDateTime } from "@/utils/format";
import {
  DOCUMENTO_CATEGORIA_LABELS,
  extractApiError,
  LOCACAO_TIMELINE_LABELS,
  fetchMaquinarioDetail,
  maquinarioDocumentoUrl,
  type LocacaoTimelineEvent,
  type LocacaoTimelineEventType,
  type MaquinarioDto,
  type MedicaoMaquinaDto,
  type MaquinarioDocumentoDto,
} from "@/services/locacoes";

type TabId = "timeline" | "medicoes" | "documentos";

interface Props {
  open: boolean;
  onClose: () => void;
  maquinario: MaquinarioDto;
}

type NoteVariant = "info" | "success" | "warning";

function noteVariant(type: LocacaoTimelineEventType): NoteVariant | null {
  if (type === "locacao_encerrada" || type === "manutencao_concluida") return "success";
  if (type === "locacao_iniciada" || type === "manutencao_aberta") return "warning";
  if (type === "maquina_criada" || type === "maquina_editada" || type === "medicao_registrada") return "info";
  if (type === "horimetro_registrado" || type === "abastecimento_registrado") return "info";
  if (type === "locacao_cancelada") return "warning";
  return null;
}

function TimelineNoteBox({ variant, children }: { variant: NoteVariant; children: ReactNode }) {
  if (variant === "success") {
    return (
      <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={2.5} />
        <p className="text-sm leading-snug text-emerald-800">{children}</p>
      </div>
    );
  }
  if (variant === "warning") {
    return (
      <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" strokeWidth={2.5} />
        <p className="text-sm leading-snug text-amber-900">{children}</p>
      </div>
    );
  }
  return (
    <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-sky-100 bg-sky-50 px-3.5 py-2.5">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" strokeWidth={2.5} />
      <p className="text-sm leading-snug text-sky-900">{children}</p>
    </div>
  );
}

function TimelineStep({ event, isLast }: { event: LocacaoTimelineEvent; isLast: boolean }) {
  const label = LOCACAO_TIMELINE_LABELS[event.type] ?? event.type;
  const boxVariant = event.note ? noteVariant(event.type) : null;

  return (
    <li className="relative flex gap-4">
      <div className="flex w-5 flex-shrink-0 flex-col items-center">
        {isLast ? (
          <div className="relative z-10 h-5 w-5 flex-shrink-0 rounded-full bg-brand-700 ring-4 ring-white" />
        ) : (
          <div className="relative z-10 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 ring-4 ring-white">
            <Check className="h-3 w-3 text-sky-600" strokeWidth={3} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 pb-7">
        <p className="text-sm font-bold leading-snug text-slate-900">{label}</p>
        <p className="mt-1 text-xs text-slate-500">
          {formatDateTime(event.at)} • {event.userName}
        </p>
        {event.note && boxVariant && <TimelineNoteBox variant={boxVariant}>{event.note}</TimelineNoteBox>}
        {event.note && !boxVariant && (
          <p className="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
            {event.note}
          </p>
        )}
      </div>
    </li>
  );
}

function MedicoesTable({ medicoes }: { medicoes: MedicaoMaquinaDto[] }) {
  if (medicoes.length === 0) {
    return <p className="py-2 text-sm text-slate-500">Nenhuma medição registrada.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Data</th>
            <th className="px-3 py-2">Horímetro</th>
            <th className="px-3 py-2">Odômetro</th>
            <th className="px-3 py-2">Usuário</th>
            <th className="px-3 py-2">Obs.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {medicoes.map((m) => (
            <tr key={m.id}>
              <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(m.dataRegistro)}</td>
              <td className="px-3 py-2 tabular-nums">
                {m.horimetroAnterior} → {m.horimetroAtual} h
              </td>
              <td className="px-3 py-2 tabular-nums">
                {m.odometroAnterior} → {m.odometroAtual} km
              </td>
              <td className="px-3 py-2">{m.usuarioNome || "—"}</td>
              <td className="px-3 py-2 text-slate-600 max-w-[10rem] truncate">{m.observacao || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentosList({ documentos, maquinaId }: { documentos: MaquinarioDocumentoDto[]; maquinaId: string }) {
  if (documentos.length === 0) {
    return <p className="py-2 text-sm text-slate-500">Nenhum documento anexado.</p>;
  }
  return (
    <ul className="space-y-2">
      {documentos.map((d) => (
        <li
          key={d.id}
          className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800">{d.originalName}</p>
            <p className="text-xs text-slate-500">
              {DOCUMENTO_CATEGORIA_LABELS[d.categoria]} • {formatDateTime(d.uploadedAt)} • {d.usuarioNome}
            </p>
          </div>
          <a
            href={maquinarioDocumentoUrl(maquinaId, d.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-sm font-medium text-brand-700 hover:underline"
          >
            Abrir
          </a>
        </li>
      ))}
    </ul>
  );
}

export function MaquinaHistoricoModal({ open, onClose, maquinario }: Props) {
  const [tab, setTab] = useState<TabId>("timeline");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [medicoes, setMedicoes] = useState<MedicaoMaquinaDto[]>([]);
  const [documentos, setDocumentos] = useState<MaquinarioDocumentoDto[]>(maquinario.documentos ?? []);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const detail = await fetchMaquinarioDetail(maquinario.id);
      setMedicoes(detail.medicoes);
      setDocumentos(detail.maquinario.documentos ?? []);
    } catch (e: unknown) {
      setMedicoes([]);
      setDocumentos(maquinario.documentos ?? []);
      setLoadError(extractApiError(e, "Não foi possível carregar medições e documentos da máquina."));
    } finally {
      setLoading(false);
    }
  }, [maquinario.id, maquinario.documentos]);

  useEffect(() => {
    if (open) {
      setTab("timeline");
      void loadDetail();
    }
  }, [open, loadDetail, maquinario.id, maquinario.timeline?.length, maquinario.updatedAt]);

  const events = [...(maquinario.timeline ?? [])].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
  );

  const tabs: Array<{ id: TabId; label: string; icon: typeof History }> = [
    { id: "timeline", label: "Auditoria", icon: History },
    { id: "medicoes", label: "Medições", icon: Gauge },
    { id: "documentos", label: "Documentos", icon: Paperclip },
  ];

  const modalTitle = (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-50">
        <History className="h-4 w-4 text-brand-700" strokeWidth={2.25} />
      </div>
      <span className="truncate font-bold text-slate-900">Histórico — {maquinario.nome}</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-end">
          <Button type="button" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {loadError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
          {loadError}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200 pb-2">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === id ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : (
        <>
          {tab === "timeline" &&
            (events.length === 0 ? (
              <p className="py-2 text-sm text-slate-500">Nenhum evento registrado.</p>
            ) : (
              <ol className="relative pl-0.5">
                {events.length > 1 && (
                  <span className="absolute bottom-6 left-[10px] top-2.5 w-px bg-slate-200" aria-hidden />
                )}
                {events.map((ev, idx) => (
                  <TimelineStep key={ev.id} event={ev} isLast={idx === events.length - 1} />
                ))}
              </ol>
            ))}

          {tab === "medicoes" && <MedicoesTable medicoes={medicoes} />}
          {tab === "documentos" && <DocumentosList documentos={documentos} maquinaId={maquinario.id} />}
        </>
      )}
    </Modal>
  );
}
